import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createTrustSafetyApi } from "../api";
import {
  appealStatusTone,
  caseStatusTone,
  enforcementStatusTone,
  formatLabel,
  reportStatusTone,
  severityTone,
} from "../labels";
import { useTrustSafetyQueue } from "../useTrustSafetyQueue";
import type {
  AppealDto,
  EnforcementDto,
  ModerationCaseDto,
  ReportDto,
} from "../types";
import {
  CreateCaseDialog,
  CreateReportDialog,
  SubmitAppealDialog,
} from "../components/TrustSafetyDialogs";

const QUEUES = [
  {
    to: "/app/trust-safety/reports",
    title: "Reports",
    body: "Intake and triage. Reporter protection is a handling constraint. Subject type filters Posts, Users, Communities — not separate case models.",
  },
  {
    to: "/app/trust-safety/cases",
    title: "Moderation Cases",
    body: "Investigate, map Policy, propose enforcement. Support Tickets stay separate.",
  },
  {
    to: "/app/trust-safety/enforcements",
    title: "Enforcement Actions",
    body: "Propose → approve → activate. Permanent ban / high-impact need Dual Approval.",
  },
  {
    to: "/app/trust-safety/appeals",
    title: "Appeals",
    body: "Independent review from the original high-impact decision.",
  },
] as const;

const REPORT_STATUSES = [
  "submitted",
  "triaged",
  "linked_or_opened",
  "resolved",
  "closed",
] as const;

const CASE_STATUSES = [
  "new",
  "triage",
  "investigating",
  "awaiting_information",
  "actioned",
  "appealed",
  "closed",
] as const;

const ENFORCEMENT_STATUSES = [
  "proposed",
  "approved",
  "active",
  "expired",
  "lifted",
  "reversed",
  "superseded",
] as const;

const APPEAL_STATUSES = [
  "submitted",
  "under_review",
  "more_information_required",
  "upheld",
  "modified",
  "reversed",
  "withdrawn",
  "closed",
] as const;

export function TrustSafetyHubPage() {
  const navigate = useNavigate();
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Trust & Safety" },
  ]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Trust & Safety"
        description="Triage Reports, investigate Moderation Cases, propose policy-grounded Enforcement Actions, and review Appeals. Community ownership and membership are not decided here. Community lifecycle stays on Communities."
      />
      <Flex gap={16} wrap>
        {QUEUES.map((queue) => (
          <Card
            key={queue.to}
            header={
              <Text role="title" as="h3">
                {queue.title}
              </Text>
            }
          >
            <Stack gap={12}>
              <Text role="small" color="secondary" as="p">
                {queue.body}
              </Text>
              <Button
                variant="secondary"
                onClick={() => void navigate(queue.to)}
              >
                Open queue
              </Button>
            </Stack>
          </Card>
        ))}
      </Flex>
    </Stack>
  );
}

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  actions,
  error,
  loading,
  empty,
  children,
  page,
  nextCursor,
  onNext,
  onPrevious,
  onRefresh,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  actions?: ReactNode;
  error: string | null;
  loading: boolean;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
}) {
  usePageBreadcrumbs(breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8}>
            {actions}
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>{filters}</Filters>
      {error ? (
        <Alert tone="danger" title={`Unable to load ${title}`}>
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && empty ? (
        <EmptyState title={`No ${title.toLowerCase()} found`} reason="filters" />
      ) : null}
      {!loading && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function ReportsQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const queue = useTrustSafetyQueue<ReportDto>(async (params) => {
    const result = await api.listReports({
      ...(params.cursor ? { cursor: params.cursor } : {}),
      limit: 25,
      ...(params.status ? { status: params.status } : {}),
      ...(params.sort ? { sort: params.sort } : { sort: "-submitted_at" }),
      ...(params.extra.report_type
        ? { report_type: params.extra.report_type }
        : {}),
      ...(params.extra.subject_type
        ? { subject_type: params.extra.subject_type }
        : {}),
      ...(params.extra.moderation_case_id
        ? { moderation_case_id: params.extra.moderation_case_id }
        : {}),
    });
    return { items: result.reports, pagination: result.pagination };
  });

  return (
    <>
      <QueueShell
        title="Reports"
        description="Intake queue. Do not expose protected reporter identity beyond authorized handling."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Trust & Safety", to: "/app/trust-safety" },
          { label: "Reports" },
        ]}
        actions={
          <Button variant="secondary" onClick={() => setCreateOpen(true)}>
            Create Report
          </Button>
        }
        filters={
          <>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...REPORT_STATUSES.map((item) => ({
                  value: item,
                  label: formatLabel(item),
                })),
              ]}
            />
            <Select
              label="Report type"
              value={queue.extra.report_type ?? ""}
              onChange={(event) =>
                queue.setExtra("report_type", event.target.value)
              }
              options={[
                { value: "", label: "All types" },
                {
                  value: "reported_post",
                  label: "reported post",
                },
                {
                  value: "reported_user",
                  label: "reported user",
                },
                {
                  value: "reported_community",
                  label: "reported community",
                },
                {
                  value: "reported_business",
                  label: "reported business",
                },
                {
                  value: "booking_concern",
                  label: "booking concern",
                },
              ]}
            />
            <Input
              label="Subject type"
              value={queue.extra.subject_type ?? ""}
              onChange={(event) =>
                queue.setExtra("subject_type", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort || "-submitted_at"}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "-submitted_at", label: "Newest first" },
                { value: "submitted_at", label: "Oldest first" },
                { value: "status", label: "Status" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Clear
            </Button>
          </>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Subject</Th>
              <Th>Submitted</Th>
              <Th>Case</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr key={row.id}>
                <Td>{formatLabel(row.report_type)}</Td>
                <Td>
                  <StatusChip tone={reportStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>
                  <Text role="bodyStrong" as="div">
                    {row.subject_type}
                  </Text>
                  <Text role="caption" color="muted" as="div">
                    {row.subject_id}
                  </Text>
                </Td>
                <Td>{row.submitted_at}</Td>
                <Td>{row.moderation_case_id ?? "—"}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void navigate(`/app/trust-safety/reports/${row.id}`)
                    }
                  >
                    Open
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreateReportDialog
        open={createOpen}
        busy={busy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setCreateError(null);
          try {
            const result = await api.createReport(input);
            toast.push({ title: "Report created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/trust-safety/reports/${result.report.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function CasesQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const queue = useTrustSafetyQueue<ModerationCaseDto>(async (params) => {
    const result = await api.listCases({
      ...(params.cursor ? { cursor: params.cursor } : {}),
      limit: 25,
      ...(params.status ? { status: params.status } : {}),
      ...(params.sort ? { sort: params.sort } : { sort: "-opened_at" }),
      ...(params.extra.severity ? { severity: params.extra.severity } : {}),
      ...(params.extra.owner_user_id
        ? { owner_user_id: params.extra.owner_user_id }
        : {}),
      ...(params.extra.market_id ? { market_id: params.extra.market_id } : {}),
    });
    return { items: result.moderation_cases, pagination: result.pagination };
  });

  return (
    <>
      <QueueShell
        title="Moderation Cases"
        description="Investigate with evidence and Policy. Enforcement is proposed separately and audited."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Trust & Safety", to: "/app/trust-safety" },
          { label: "Cases" },
        ]}
        actions={
          <Button variant="secondary" onClick={() => setCreateOpen(true)}>
            Open Case
          </Button>
        }
        filters={
          <>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...CASE_STATUSES.map((item) => ({
                  value: item,
                  label: formatLabel(item),
                })),
              ]}
            />
            <Select
              label="Severity"
              value={queue.extra.severity ?? ""}
              onChange={(event) =>
                queue.setExtra("severity", event.target.value)
              }
              options={[
                { value: "", label: "All severity" },
                { value: "low", label: "low" },
                { value: "medium", label: "medium" },
                { value: "high", label: "high" },
                { value: "critical", label: "critical" },
              ]}
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Input
              label="Market ID"
              value={queue.extra.market_id ?? ""}
              onChange={(event) =>
                queue.setExtra("market_id", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort || "-opened_at"}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "-opened_at", label: "Newest first" },
                { value: "opened_at", label: "Oldest first" },
                { value: "severity", label: "Severity" },
                { value: "status", label: "Status" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Clear
            </Button>
          </>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Case ID</Th>
              <Th>Status</Th>
              <Th>Severity</Th>
              <Th>Owner</Th>
              <Th>Opened</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr key={row.id}>
                <Td>{row.id}</Td>
                <Td>
                  <StatusChip tone={caseStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>
                  <StatusChip tone={severityTone(row.severity)}>
                    {formatLabel(row.severity)}
                  </StatusChip>
                </Td>
                <Td>{row.owner_user_id ?? "Unassigned"}</Td>
                <Td>{row.opened_at}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void navigate(`/app/trust-safety/cases/${row.id}`)
                    }
                  >
                    Open
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreateCaseDialog
        open={createOpen}
        busy={busy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setCreateError(null);
          try {
            const result = await api.createCase(input);
            toast.push({ title: "Case opened", tone: "success" });
            setCreateOpen(false);
            void navigate(
              `/app/trust-safety/cases/${result.moderation_case.id}`,
            );
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function EnforcementsQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const navigate = useNavigate();

  const queue = useTrustSafetyQueue<EnforcementDto>(async (params) => {
    const result = await api.listEnforcements({
      ...(params.cursor ? { cursor: params.cursor } : {}),
      limit: 25,
      ...(params.status ? { status: params.status } : {}),
      ...(params.sort ? { sort: params.sort } : { sort: "-effective_from" }),
      ...(params.extra.action_type
        ? { action_type: params.extra.action_type }
        : {}),
      ...(params.extra.moderation_case_id
        ? { moderation_case_id: params.extra.moderation_case_id }
        : {}),
      ...(params.extra.subject_type
        ? { subject_type: params.extra.subject_type }
        : {}),
    });
    return {
      items: result.enforcement_actions,
      pagination: result.pagination,
    };
  });

  return (
    <QueueShell
      title="Enforcement Actions"
      description="Subject-focused authorized history. Act from the linked Moderation Case for evidence and Policy context."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "Trust & Safety", to: "/app/trust-safety" },
        { label: "Enforcements" },
      ]}
      filters={
        <>
          <Select
            label="Status"
            value={queue.status}
            onChange={(event) => queue.setStatus(event.target.value)}
            options={[
              { value: "", label: "All statuses" },
              ...ENFORCEMENT_STATUSES.map((item) => ({
                value: item,
                label: formatLabel(item),
              })),
            ]}
          />
          <Select
            label="Action type"
            value={queue.extra.action_type ?? ""}
            onChange={(event) =>
              queue.setExtra("action_type", event.target.value)
            }
            options={[
              { value: "", label: "All types" },
              {
                value: "no_action",
                label: "no action",
              },
              {
                value: "content_removal",
                label: "content removal",
              },
              { value: "warning", label: "warning" },
              {
                value: "capability_restriction",
                label: "capability restriction",
              },
              {
                value: "temporary_suspension",
                label: "temporary suspension",
              },
              {
                value: "permanent_ban",
                label: "permanent ban",
              },
              {
                value: "community_restriction",
                label: "community restriction",
              },
              {
                value: "escalation_to_senior_review",
                label: "escalation to senior review",
              },
            ]}
          />
          <Input
            label="Case ID"
            value={queue.extra.moderation_case_id ?? ""}
            onChange={(event) =>
              queue.setExtra("moderation_case_id", event.target.value)
            }
          />
          <Button variant="secondary" onClick={() => queue.clearFilters()}>
            Clear
          </Button>
        </>
      }
      error={queue.error}
      loading={queue.loading}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onNext={queue.goNext}
      onPrevious={queue.goPrevious}
      onRefresh={() => void queue.load()}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Subject</Th>
            <Th>Case</Th>
            <Th>Approved by</Th>
            <Th>Effective</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr key={row.id}>
              <Td>{formatLabel(row.action_type)}</Td>
              <Td>
                <StatusChip tone={enforcementStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>
                {row.subject_type}
                <Text role="caption" color="muted" as="div">
                  {row.subject_id}
                </Text>
              </Td>
              <Td>{row.moderation_case_id}</Td>
              <Td>{row.approved_by_user_id ?? "—"}</Td>
              <Td>{row.effective_from}</Td>
              <Td>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() =>
                    void navigate(
                      `/app/trust-safety/cases/${row.moderation_case_id}`,
                    )
                  }
                >
                  Open case
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function AppealsQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const queue = useTrustSafetyQueue<AppealDto>(async (params) => {
    const result = await api.listAppeals({
      ...(params.cursor ? { cursor: params.cursor } : {}),
      limit: 25,
      ...(params.status ? { status: params.status } : {}),
      ...(params.sort ? { sort: params.sort } : { sort: "-submitted_at" }),
      ...(params.extra.source_type
        ? { source_type: params.extra.source_type }
        : {}),
      ...(params.extra.source_id ? { source_id: params.extra.source_id } : {}),
      ...(params.extra.assigned_reviewer_user_id
        ? {
            assigned_reviewer_user_id: params.extra.assigned_reviewer_user_id,
          }
        : {}),
    });
    return { items: result.appeals, pagination: result.pagination };
  });

  return (
    <>
      <QueueShell
        title="Appeals"
        description="Independent review. Reviewer must differ from the original enforcement approver when the source is an Enforcement Action."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Trust & Safety", to: "/app/trust-safety" },
          { label: "Appeals" },
        ]}
        actions={
          <Button variant="secondary" onClick={() => setCreateOpen(true)}>
            Submit Appeal
          </Button>
        }
        filters={
          <>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...APPEAL_STATUSES.map((item) => ({
                  value: item,
                  label: formatLabel(item),
                })),
              ]}
            />
            <Select
              label="Source type"
              value={queue.extra.source_type ?? ""}
              onChange={(event) =>
                queue.setExtra("source_type", event.target.value)
              }
              options={[
                { value: "", label: "All sources" },
                {
                  value: "enforcement_action",
                  label: "enforcement action",
                },
                {
                  value: "moderation_case",
                  label: "moderation case",
                },
              ]}
            />
            <Input
              label="Assigned reviewer"
              value={queue.extra.assigned_reviewer_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("assigned_reviewer_user_id", event.target.value)
              }
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Clear
            </Button>
          </>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Source</Th>
              <Th>Status</Th>
              <Th>Appellant</Th>
              <Th>Reviewer</Th>
              <Th>Submitted</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr key={row.id}>
                <Td>
                  {formatLabel(row.source_type)}
                  <Text role="caption" color="muted" as="div">
                    {row.source_id}
                  </Text>
                </Td>
                <Td>
                  <StatusChip tone={appealStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>{row.appellant_user_id}</Td>
                <Td>{row.assigned_reviewer_user_id ?? "—"}</Td>
                <Td>{row.submitted_at}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void navigate(`/app/trust-safety/appeals/${row.id}`)
                    }
                  >
                    Open
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <SubmitAppealDialog
        open={createOpen}
        busy={busy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setCreateError(null);
          try {
            const result = await api.submitAppeal(input);
            toast.push({ title: "Appeal submitted", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/trust-safety/appeals/${result.appeal.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
